import { useState, useEffect } from "react";
import { Grid, Card, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CategoriesApi from "api/categories";
import { handleUpload } from "utils/ImageUploader";
import SoftImageInput from "components/SoftImageInput";

function ModifierMainCategory() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [image, setImage] = useState("");
    const [file, setFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [imageLoading, setImageLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchCategory = async () => {
            const token = localStorage.getItem("token");
            try {
                const response = await CategoriesApi.GetMainCategoryById(token, id);
                const data = response.data;
                setName(data.main_category_name);
                setDescription(data.description || "");
                setImage(data.main_category_image || "");
            } catch (err) {
                console.error("Failed to fetch category:", err);
                setError(t("Erreur lors du chargement de la catégorie"));
            } finally {
                setIsLoading(false);
            }
        };

        fetchCategory();
    }, [id, t]);

    const handleImageChange = async (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            try {
                setImageLoading(true);
                const imageUrl = await handleUpload(selectedFile);
                setImage(imageUrl);
                setFile(imageUrl);
            } catch (err) {
                console.error("Error uploading image:", err);
                setError(t("Erreur lors de l'upload de l'image"));
            } finally {
                setImageLoading(false);
            }
        }
    };

    const handleSave = async () => {
        if (!name) {
            setError(t("Le nom est requis"));
            return;
        }

        setIsSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            let imageUrl = file || image;

            const formData = new FormData();
            formData.append("main_category_name", name);
            formData.append("description", description);
            if (imageUrl) {
                formData.append("main_category_image", imageUrl);
            }

            await CategoriesApi.UpdateMainCategory(token, id, formData);
            navigate("/gestion-de-stock/main-categories");
        } catch (err) {
            console.error("Failed to update category:", err);
            setError(t("Erreur lors de la modification de la catégorie"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox mt={3} mb={4}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} lg={8}>
                        <Card>
                            <SoftBox p={3}>
                                <SoftTypography variant="h5" fontWeight="bold">
                                    {t("Modifier la catégorie")}
                                </SoftTypography>
                                {isLoading ? (
                                    <SoftBox display="flex" justifyContent="center" p={5}>
                                        <CircularProgress size={40} color="info" />
                                    </SoftBox>
                                ) : (
                                    <SoftBox mt={3}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        {t("Nom")}
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftInput
                                                    placeholder={t("Nom")}
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    error={!!error && !name}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        {t("Description")}
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftInput
                                                    placeholder={t("Description")}
                                                    value={description}
                                                    onChange={(e) => setDescription(e.target.value)}
                                                    multiline
                                                    rows={3}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <SoftImageInput
                                                    label={t("Image")}
                                                    image={image}
                                                    onChange={handleImageChange}
                                                    onClear={() => {
                                                        setImage("");
                                                        setFile(null);
                                                    }}
                                                    loading={imageLoading}
                                                />
                                            </Grid>
                                        </Grid>
                                    </SoftBox>
                                )}
                                {error && (
                                    <SoftBox mt={2}>
                                        <SoftTypography variant="caption" color="error">
                                            {error}
                                        </SoftTypography>
                                    </SoftBox>
                                )}
                                <SoftBox mt={4} display="flex" justifyContent="space-between">
                                    <SoftButton variant="gradient" color="secondary" onClick={() => navigate("/gestion-de-stock/main-categories")}>
                                        {t("Retour")}
                                    </SoftButton>
                                    <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={isSaving || isLoading}>
                                        {isSaving ? <CircularProgress size={20} color="inherit" /> : t("Enregistrer")}
                                    </SoftButton>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>
                </Grid>
            </SoftBox>
            <Footer />
        </DashboardLayout>
    );
}

export default ModifierMainCategory;
